import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Helmet } from 'react-helmet';

const isServer = typeof window === 'undefined';

export default function RichSnippets({
  product,
  article,
  breadcrumb,
  contact,
  faq,
  logo,
  ldjson,
  children,
  ...props
}) {
  const myWindow = !isServer && (window.srcMap || window);
  const imageServerMap = isServer && global.images;
  const imageMap = imageServerMap || myWindow;
  // The source of this query is explicitly defined in the gatsby-config.js
  const {
    site: {
      siteMetadata: { siteUrl, title: brand }
    }
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          title
          siteUrl
        }
      }
    }
  `);
  const {
    title,
    excerpt,
    activePrice,
    slug,
    image,
    notAvailable,
    initialFlags
  } = props;

  const sanitizeText = string => {
    return (
      string &&
      string
        .replace(/"/gm, '\\"')
        .replace(/’/gm, "'")
        .replace(/“/gm, '\\"')
        .replace(/”/gm, '\\"')
    );
  };
  const stockState = notAvailable || initialFlags ? 'SoldOut' : 'InStock';
  // schemas
  const productSchema = `{
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": "${title}",
    "image": "${siteUrl}${imageMap[image]?.originalImg}",
    "description": "${sanitizeText(
      excerpt?.body?.replace(/<[^>]*>/g, '')?.replace(/\n/g, '')
    )}",
    "brand": {
      "@type": "Thing",
      "name": "${brand}"
    },
    "offers": {
      "@type": "Offer",
      "url": "${siteUrl}/product/${slug}",
      "priceCurrency": "USD",
      "price": "${activePrice}",
      "itemCondition": "https://schema.org/NewCondition",
      "availability": "https://schema.org/${stockState}",
      "seller": {
        "@type": "Organization",
        "name": "${brand}"
      }
    }
  }`;
  // 2015-02-05T08:00:00+08:00
  const {
    author,
    datePublished,
    dateModified,
    articleExcerpt,
    articleImage
  } = props;
  const articleSchema = `{
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "${siteUrl}${slug}"
    },
    "headline": "${title}",
    "image": "${siteUrl}${imageMap[(articleImage?.title)]?.originalImg}",
    "datePublished": "${datePublished}",
    "dateModified": "${dateModified}",
    "author": {
      "@type": "Person",
      "name": "${sanitizeText(author)}"
    },
     "publisher": {
      "@type": "Organization",
      "name": "${brand}",
      "logo": {
        "@type": "ImageObject",
        "url": "${siteUrl}/images/logo.jpg"
      }
    },
    "description": "${sanitizeText(
      articleExcerpt?.replace(/<[^>]*>/g, '')?.replace(/\n/g, '')
    )}"
  }`;

  const { breadcrumbs } = props;

  const breadcrumbSchema = `{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [${breadcrumbs?.map(
      ({ name, url }, i) =>
        `
          {
        "@type": "ListItem",
        "position": ${i + 1},
        "name": "${name}",
        "item": "${url}"
          }
        `
    )}]
    }`;
  const { faqs } = props;
  const faqSchema = `{
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [${faqs?.map(
        ({ question, answer }) => `{
        "@type": "Question",
        "name": "${sanitizeText(question)}",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "${sanitizeText(answer)}"
        }
      }`
      )}]
      }`;
  const { corporateContact } = props;
  const logoSchema = `{
    "@context": "https://schema.org",
    "@type": "Organization",
    "url": "${siteUrl}",
    "logo": "${siteUrl}/images/${logo}"
  }`;

  const contactSchema = `${corporateContact}`;

  const useSchema = () => {
    if (product) return productSchema;
    if (logo) return logoSchema;
    if (article) return articleSchema;
    if (breadcrumbs) return breadcrumbSchema;
    if (faqs) return faqSchema;
    if (corporateContact) return contactSchema;
    return null;
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {useSchema()?.replace(/\n/gm, '')}
      </script>
    </Helmet>
  );
}
